import React from 'react';
import { Typography, Progress, Tag, Spin, Empty } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;

// Tampilan "kotak fisik" virtual untuk box notice yang sedang dipegang
// sebuah UPT - ilustrasi SVG inline (bukan file/network asset, jadi tetap
// ringan) ditambah "label" HTML terpisah di bawahnya untuk info (teks tetap
// lurus/mudah dibaca). Dipakai di halaman Pengaturan Notice
// (fe/src/views/notice/Notice.jsx) dan modal Pengaturan Notice yang muncul
// otomatis setelah login (AdminLayout.jsx) - disatukan di sini supaya kedua
// tempat selalu konsisten.
// Palet karton per status - "aktif" sedikit lebih hangat/jenuh warnanya,
// "dialokasikan" (cadangan) sedikit pudar/keabu-abuan supaya kelihatan beda
// dari jauh tanpa harus membaca label dulu.
const BOX_VISUAL_THEME = {
	aktif: { top: '#e3ad6d', front: '#c4854c', side: '#9c652f', interior: '#5c3a1e', tape: '#f1dcb8', accent: '#52c41a', label: 'Aktif Dipakai' },
	dialokasikan: { top: '#cdc3b3', front: '#a99c87', side: '#80735f', interior: '#5c3a1e', tape: '#e6ddc9', accent: '#faad14', label: 'Cadangan' },
	diterima: { top: '#d8bdf0', front: '#b491d9', side: '#8a66ad', interior: '#5c3a1e', tape: '#ecd9fa', accent: '#531dab', label: 'Diterima (Belum Ditempatkan)' },
	gudang: { top: '#b9c4d6', front: '#8fa0ba', side: '#6b7c97', interior: '#5c3a1e', tape: '#dde4ee', accent: '#1e4d8a', label: 'Di Gudang Pusat' },
};

// Koordinat box isometrik (proyeksi baku: titik atas, lalu kiri/kanan/depan
// di sekeliling diamond atas, diekstrusi ke bawah sejauh BOX_H). Dihitung
// manual sebagai angka tetap - bukan transform 3D - supaya pasti render
// persis seperti yang ditulis, tidak bergantung urutan/tanda rotasi CSS.
// viewBox dimulai dari y negatif supaya ada ruang untuk flap yang terbuka
// (status 'aktif') menjulang ke atas tanpa memotong gambar.
const T  = [100, 30];   // titik belakang-atas (puncak diamond)
const L  = [25, 60];    // kiri-atas
const R  = [175, 60];   // kanan-atas
const F  = [100, 90];   // depan-atas (sudut box paling dekat ke pengamat)
const BOX_H = 95;
const L_B = [L[0], L[1] + BOX_H];
const R_B = [R[0], R[1] + BOX_H];
const F_B = [F[0], F[1] + BOX_H];
const VIEWBOX = '-10 -55 220 250';
const pts = (...p) => p.map((xy) => xy.join(',')).join(' ');

function BoxIllustration({ theme, open }) {
	return (
		<svg viewBox={VIEWBOX} width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
			{open ? (
				<>
					{/* Lubang/interior gelap - box sedang dipakai, terbuka mengangah */}
					<polygon points={pts(T, R, F, L)} fill={theme.interior} />
					{/* Flap belakang-kiri terbuka, menjulang ke atas */}
					<polygon points={pts(T, L, [-5, 5], [60, -25])} fill={theme.top} stroke="rgba(0,0,0,0.18)" strokeWidth={1} />
					{/* Flap belakang-kanan terbuka, menjulang ke atas */}
					<polygon points={pts(T, R, [205, 5], [140, -25])} fill={theme.top} stroke="rgba(0,0,0,0.18)" strokeWidth={1} />
				</>
			) : (
				<>
					{/* Sisi atas tertutup rapat */}
					<polygon points={pts(T, R, F, L)} fill={theme.top} stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
					{/* Garis lipatan flap dekat puncak, ciri khas box tertutup */}
					<line x1={L[0] + 12} y1={L[1] + 14} x2={F[0]} y2={F[1]} stroke="rgba(0,0,0,0.18)" strokeWidth={1.5} />
					<line x1={R[0] - 12} y1={R[1] + 14} x2={F[0]} y2={F[1]} stroke="rgba(0,0,0,0.22)" strokeWidth={1.5} />
				</>
			)}
			{/* Badan box - sama untuk kedua status */}
			<polygon points={pts(L, F, F_B, L_B)} fill={theme.front} stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
			<polygon points={pts(R, F, F_B, R_B)} fill={theme.side} stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
			{/* Strip lakban menyilang di tengah sambungan kedua sisi depan */}
			<polygon points={pts([94, F[1] + 2], [106, F[1] + 2], [106, F_B[1] - 2], [94, F_B[1] - 2])} fill={theme.tape} opacity={0.85} />
		</svg>
	);
}

// Tinggi kartu DIPATOK SAMA untuk semua status - supaya grid box aktif &
// cadangan tetap rapi sejajar walau isinya beda (aktif punya baris progress
// bar tambahan, cadangan tidak). Kolom kanan pakai flex column + space-between
// supaya selisih jumlah baris itu otomatis "diserap" oleh jarak antar baris,
// bukan bikin kartu jadi beda tinggi.
const CARD_HEIGHT = 184;

export function BoxVisualCard({ box, antrian, actions }) {
	const theme = BOX_VISUAL_THEME[box.status] || BOX_VISUAL_THEME.dialokasikan;
	const isAktif = box.status === 'aktif';
	const kapasitas = box.nomor_akhir - box.nomor_awal + 1;
	const sisa = box.sisa_notice;
	const persen = (sisa != null && kapasitas) ? Math.round((sisa / kapasitas) * 100) : null;
	const barColor = persen == null ? '#bfbfbf' : (persen <= 10 ? '#ff4d4f' : (persen <= 30 ? '#fa8c16' : '#52c41a'));
	const tanggal = box.dialokasikan_at ? moment(box.dialokasikan_at).format('DD MMM YYYY') : '-';

	return (
		<div style={{
			display: 'flex',
			alignItems: 'stretch',
			gap: 14,
			height: CARD_HEIGHT,
			borderRadius: 12,
			background: '#fff',
			border: '1px solid #ececec',
			boxShadow: '0 6px 16px rgba(15,40,71,0.08)',
			padding: '14px',
			marginBottom: 16,
		}}>
			{/* Ikon box murni dekoratif - terbuka/tertutup sesuai status, TANPA
			    teks di atasnya (badan box berbentuk jajaran genjang/miring, teks
			    apapun di atasnya selalu kelihatan "nempel asal" - lihat percobaan
			    sebelumnya). Semua informasi teks ada di kolom kanan. */}
			<div style={{ width: 78, flexShrink: 0, alignSelf: 'center' }}>
				<div style={{ aspectRatio: '220 / 250' }}>
					<BoxIllustration theme={theme} open={isAktif} />
				</div>
			</div>
			<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
				<div>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
						<span style={{ fontWeight: 700, fontSize: 14, fontFamily: 'monospace', color: '#262626' }}>
							{box.kode_notice}-{box.nomor_box}
						</span>
						<Tag style={{
							borderRadius: 20, border: 'none', background: theme.accent,
							color: '#1f1f0f', fontWeight: 700, fontSize: 11, flexShrink: 0,
						}}>
							{box.status === 'dialokasikan' ? `Cadangan #${antrian}` : theme.label}
						</Tag>
					</div>
					<div style={{ fontSize: 12, color: '#8c8c8c', fontFamily: 'monospace', marginTop: 4 }}>
						{String(box.nomor_awal).padStart(7, '0')} – {String(box.nomor_akhir).padStart(7, '0')}
					</div>
				</div>
				{isAktif && (
					<div>
						<Progress percent={persen ?? 0} showInfo={false} strokeColor={barColor} trailColor="#f0f0f0" size="small" />
						<div style={{ fontSize: 12, marginTop: 4, color: '#595959' }}>
							{sisa != null ? `${sisa} lembar tersisa` : 'Sisa stok tidak tersedia'}
						</div>
					</div>
				)}
				<div style={{ fontSize: 11, color: '#bfbfbf' }}>
					{isAktif ? 'Aktif sejak' : 'Dialokasikan'} {tanggal}
				</div>
				{actions && (
					<div style={{ paddingTop: 8, borderTop: '1px solid #f5f5f5', display: 'flex', gap: 8 }}>
						{actions}
					</div>
				)}
			</div>
		</div>
	);
}

export default function BoxVisualList({ listBox, loading, title = 'Box Notice UPT Ini', emptyText = 'Belum ada box notice untuk UPT ini' }) {
	const aktifList = listBox.filter((b) => b.status === 'aktif');
	const cadanganList = listBox.filter((b) => b.status === 'dialokasikan').sort((a, b) => a.nomor_box - b.nomor_box);

	return (
		<div>
			{title && (
				<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
					<InboxOutlined style={{ color: '#1e4d8a', fontSize: 18 }} />
					<Text strong style={{ fontSize: 14, color: '#0f2847' }}>{title}</Text>
				</div>
			)}
			{loading ? (
				<div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
			) : listBox.length === 0 ? (
				<Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
			) : (
				<>
					{aktifList.map((b) => <BoxVisualCard key={b.id} box={b} />)}
					{cadanganList.map((b, i) => <BoxVisualCard key={b.id} box={b} antrian={i + 1} />)}
				</>
			)}
		</div>
	);
}
